import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { MAX_VIEW_SCALE, MIN_VIEW_SCALE } from '../../constants';
import { useAppState } from '../../context/AppState/useAppState';
import {
  getZoomPercentage,
  scaleFromSlider,
  sliderFromScale,
  ZOOM_PRESETS,
} from './zoomSlider';

const EditContainer = styled.div`
  position: absolute;
  bottom: 0px;
  right: 24px;
  z-index: 2;

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 56px;
`;

const ZoomValueWrapper = styled.div`
  position: relative;
  margin: 0;
`;

const ZoomValue = styled.button`
  font-size: 14px;
  color: var(--text-muted);
  font-weight: 500;
  line-height: 14.52px;
  text-align: left;
  background: none;
  border: none;
  padding: 0.5rem;
  border-radius: 2px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: var(--text);
    background: var(--surface-elevated);
  }
`;

const ZoomDropdown = styled.ul`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  list-style: none;
  padding: 4px 0;
  margin-left: 0;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 6px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.12);
  min-width: 80px;
  z-index: 10;
`;

const ZoomOption = styled.li<{ $active: boolean }>`
  padding: 6px 12px;
  font-size: 14px;
  color: ${(p) => (p.$active ? 'var(--text)' : 'var(--text-muted)')};
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: var(--surface);
  }
`;

const StyledSlider = styled.input<{ $progress: number }>`
  -webkit-appearance: none;
  appearance: none;
  width: 240px;
  height: 4px;
  background: linear-gradient(
    to right,
    var(--border-dark) ${(p) => p.$progress}%,
    var(--border) ${(p) => p.$progress}%
  );
  border-radius: 2px;
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--surface);
    border: solid 1px var(--border);
    cursor: pointer;
  }

  &:hover::-webkit-slider-thumb {
    border-color: var(--border-dark);
    background: var(--surface-elevated);
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--surface);
    border: solid 1px var(--border);
    cursor: pointer;
  }

  &:hover::-moz-range-thumb {
    border-color: var(--border-dark);
    background: var(--surface-elevated);
  }

  &::-moz-range-progress {
    background: var(--border-dark);
    border-radius: 2px;
    height: 4px;
  }
`;

export const SettingsBar = () => {
  const { viewBox, setViewBox } = useAppState();
  const { scale } = viewBox;
  const zoomValue = getZoomPercentage(scale);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updateViewBox = (newScale: number) => {
    const clamped = Math.min(
      MAX_VIEW_SCALE,
      Math.max(MIN_VIEW_SCALE, newScale)
    );
    setViewBox((vB) => {
      const w = window.innerWidth * clamped;
      const h = window.innerHeight * clamped;
      // Anchor on the viewport centre, the way cursor zoom anchors the pointer.
      return {
        ...vB,
        x: vB.x + (vB.w - w) / 2,
        y: vB.y + (vB.h - h) / 2,
        w,
        h,
        scale: clamped,
      };
    });
  };

  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  const sliderValue = sliderFromScale(scale);
  const progress = sliderValue * 100;

  return (
    <EditContainer id="edit">
      <StyledSlider
        $progress={progress}
        type="range"
        min={0}
        max={1}
        step={0.001}
        value={sliderValue}
        onChange={(e) => updateViewBox(scaleFromSlider(Number(e.target.value)))}
      />
      <ZoomValueWrapper ref={dropdownRef}>
        <ZoomValue onClick={() => setIsDropdownOpen((prev) => !prev)}>
          {zoomValue + '%'}
        </ZoomValue>
        {isDropdownOpen && (
          <ZoomDropdown>
            {ZOOM_PRESETS.map((preset) => (
              <ZoomOption
                key={preset.label}
                $active={Math.abs(scale - preset.scale) < 1e-6}
                onClick={() => {
                  updateViewBox(preset.scale);
                  setIsDropdownOpen(false);
                }}
              >
                {preset.label}
              </ZoomOption>
            ))}
          </ZoomDropdown>
        )}
      </ZoomValueWrapper>
    </EditContainer>
  );
};
